import React from 'react'
import IEventedComponent, { messageTypes } from '../components/IEventedComponent'
import Dropzone from 'react-dropzone'
import shp from 'shpjs'
import JSZip from 'jszip'
import rewind from '@turf/rewind'
import styled from 'styled-components'
import colours from '../lib/colours'
import Spinner from '../components/spinner'
import { CloseFileUploadDialogEvent, EnableParcelSelectionModeEvent, UploadedShapefileEvent } from '../lib/messages'
import Dialog from './dialog'
import FileUploadGuidance from '../content/file-upload-guidance.mdx'
import Callout from '../components/callout'
import { acceptedFileTypesForUpload } from '../lib/constants'

const StyledDropZoneContainer = styled.div`
  border: 3px dashed ${colours.aqua};
  padding: 40px 10px;
  margin: 20px 0;
  text-align: center;
  vertical-align: middle;
  font-weight: 500;
  cursor: pointer;
`

const CalloutContainer = styled.div`
  margin: -20px -40px 0;
  padding: 10px 20px 10px 15px;
  font-weight: 300;
`

interface IProps {
  open: boolean
}

interface IState {
  droppedFiles: any[]
  invalidFiles: any[]
  importError: boolean
  isImporting: boolean
}

const UploadFileDialog = class extends IEventedComponent<IProps, IState> {
  state = {
    droppedFiles: [],
    invalidFiles: [],
    importError: false,
    isImporting: false
  }

  clearDroppedFiles() {
    this.setState({
      droppedFiles: [],
      invalidFiles: [],
      importError: false,
      isImporting: false
    })
  }

  handleCloseAndEnableParcelSelectionMode() {
    this.publish<EnableParcelSelectionModeEvent>(messageTypes.onEnableParcelSelectionMode, {})
    this.handleClose()
  }

  handleClose() {
    this.clearDroppedFiles()
    this.publish<CloseFileUploadDialogEvent>(messageTypes.onCloseFileUploadDialog, {})
  }

  handleClear() {
    this.clearDroppedFiles()
  }

  handleOpenState(open: boolean) {
    if (!open) {
      this.handleCloseAndEnableParcelSelectionMode()
    }
  }

  validateSingleFileType(fileNames: string[], fileType: string) {
    return fileNames.filter((fileName: string) => fileName.endsWith(fileType)).length === 1
  }

  validateZipFileContents(file: any) {
    let component = this
    return new Promise((resolve, reject) => {
      JSZip.loadAsync(file).then((zip: any) => {
        const fileNames = Object.keys(zip.files)
        if (
          !component.validateSingleFileType(fileNames, '.shp') ||
          !component.validateSingleFileType(fileNames, '.shx') ||
          !component.validateSingleFileType(fileNames, '.dbf') ||
          !component.validateSingleFileType(fileNames, '.prj')
        ) {
          reject('Invalid Zip File Contents')
        }
        resolve(true)
      })
    })
  }

  readAndConvertShapefile(file: any) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const buffer = reader.result
        shp(buffer).then(
          (geojson: any) => {
            if (Array.isArray(geojson)) {
              reject('Multiple files in uploaded zip file')
            } else {
              resolve(rewind(geojson))
            }
          },
          (reason: any) => {
            reject(reason)
          }
        )
      }

      reader.onabort = () => {
        reject('File reading aborted: ' + file.name)
      }

      reader.onerror = () => {
        reject('File reading error: ' + file.name)
      }

      reader.readAsArrayBuffer(file)
    })
  }

  startImport() {
    this.setState({
      isImporting: true
    })
  }

  endImport(results: any) {
    this.setState({
      isImporting: false
    })
    if (results) {
      this.publish<UploadedShapefileEvent>(messageTypes.onShapefileUploaded, {
        geojson: results
      })
    }
  }

  handleImport() {
    let component = this
    component.startImport()

    let geojsonPromises: Promise<any>[] = []
    component.state.droppedFiles.forEach(file => {
      geojsonPromises.push(component.validateZipFileContents(file).then(() => component.readAndConvertShapefile(file)))
    })

    Promise.all(geojsonPromises).then(
      results => {
        component.handleClose()
        component.endImport(results[0])
      },
      reasons => {
        console.log(reasons)
        component.setState({
          importError: true
        })
        component.endImport(null)
      }
    )
  }

  handleDropFiles(files: any) {
    this.clearDroppedFiles()
    let droppedFiles: any[] = this.state.droppedFiles.slice()
    let invalidFiles: any[] = []
    files.forEach((file: any) => {
      window.URL.revokeObjectURL(file.preview)
      if (acceptedFileTypesForUpload.indexOf(file.type) !== -1) {
        droppedFiles.push(file)
      } else {
        invalidFiles.push(file)
      }
    })
    this.setState({
      droppedFiles,
      invalidFiles
    })
  }

  renderInvalidFilesCallout() {
    const { droppedFiles, invalidFiles } = this.state
    if (droppedFiles.length || !invalidFiles.length) return null
    return (
      <div style={{ marginBottom: '20px', fontWeight: 300 }}>
        <Callout type="error">Only .zip files are supported</Callout>
      </div>
    )
  }

  render() {
    let dropTitle = 'Add Layer from File'
    let importingTitle = 'Importing...'

    let importContent = this.state.droppedFiles.map((droppedFile: any) => {
      return <div key={Math.random().toString()}>{droppedFile.name}</div>
    })

    let dropzoneContent = (
      <Dropzone onDrop={this.handleDropFiles.bind(this)} multiple={false}>
        {({ getRootProps, getInputProps }) => (
          <StyledDropZoneContainer {...getRootProps()}>
            {this.state.droppedFiles.length ? importContent : 'Drag and drop a zipped shapefile or click to select'}
            <input {...getInputProps()} />
          </StyledDropZoneContainer>
        )}
      </Dropzone>
    )

    let dropContent = (
      <div>
        <CalloutContainer style={{ backgroundColor: colours.lightGreenBackground }}>
          <Callout type="warning">
            This feature should only be used for areas of Crown Land or land being acquired through a s.35 subdivision
          </Callout>
        </CalloutContainer>
        <FileUploadGuidance />
        {dropzoneContent}
        {this.renderInvalidFilesCallout()}
      </div>
    )

    let importingContent = (
      <div style={{ marginLeft: '50%' }}>
        <Spinner />
      </div>
    )

    let errorContent = (
      <CalloutContainer>
        <Callout type="error">
          There was an error importing the shapefile. Please ensure:
          <ul>
            <li>There is only a single shapefile within the selected zip file</li>
            <li>The shapefile contains .shp, .shx, .dbf and .prj files</li>
          </ul>
        </Callout>
      </CalloutContainer>
    )

    let title
    let content
    if (this.state.importError) {
      title = dropTitle
      content = errorContent
    } else if (this.state.isImporting) {
      title = importingTitle
      content = importingContent
    } else {
      title = dropTitle
      content = dropContent
    }

    const additionalButtons: any[] = [
      {
        onClickHandler: this.clearDroppedFiles.bind(this),
        text: 'Clear File',
        disabled: this.state.droppedFiles.length === 0
      },
      {
        onClickHandler: this.handleImport.bind(this),
        text: 'Import Layer',
        primary: true,
        disabled: this.state.droppedFiles.length === 0
      }
    ]
    return (
      <Dialog
        open={this.props.open}
        heading={title}
        setPopupState={this.handleOpenState.bind(this)}
        additionalButtons={this.state.isImporting || this.state.importError ? [] : additionalButtons}
      >
        {content}
      </Dialog>
    )
  }
}

export default UploadFileDialog
